import './App.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';

const VENTURES = [
  { id: 'lgs', name: 'LGS', filename: 'LGS-logo.png' },
  { id: 'place', name: 'PLACE', filename: 'PLACELogo2.png' },
  { id: 'keyskin', name: 'Key Skin', filename: 'keyskin.png' },
  { id: 'amelle', name: 'AMELLE', filename: 'Amelle.PNG' },
  { id: 'oxford', name: 'University of Oxford', filename: 'oxford.png' },
  { id: 'model', name: 'Model', filename: 'Model.png' },
  { id: '311alan-tech', name: '311ALAN Tech', filename: '311AlanTechcropped.png' },
  { id: 'ourbazaar', name: 'Our Bazaar', filename: 'OurBazaar.png' },
];

const LOGO_LINKS = {
  keyskin: 'https://www.thekeyskin.com/',
  lgs: 'https://lgsdefence.edu.pk/',
  amelle: 'https://pk.amelle.skin/',
  oxford: 'https://www.maths.ox.ac.uk/',
  ourbazaar: 'https://ourbazaar.pk/',
  place: 'https://www.instagram.com/AMELLE.Place/',
};

function openExternalLink(href) {
  if (!href) return;

  // New tab only (no same-tab fallback).
  const a = document.createElement('a');
  a.href = href;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.click();
}

function getPublicLogoUrl(filename) {
  return encodeURI(`${process.env.PUBLIC_URL}/logos/${filename}`);
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(Boolean(media.matches));
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  return reduced;
}

function LogoTicker({ ventures, axis = 'x', secondsPerLoop = 160, onLogoClick }) {
  const prefersReducedMotion = usePrefersReducedMotion();

  const viewportRef = useRef(null);
  const trackRef = useRef(null);

  const offsetRef = useRef(0);
  const baseWidthRef = useRef(1);
  const lastTimeRef = useRef(0);
  const rafRef = useRef(null);

  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartOffsetRef = useRef(0);
  const dragLastTimeRef = useRef(0);
  const dragLastOffsetRef = useRef(0);
  const dragVelocityRef = useRef(0);
  const dragMovedRef = useRef(false);
  const suppressNextClickRef = useRef(false);
  const pointerIdRef = useRef(null);

  const momentumActiveRef = useRef(false);
  const momentumVelocityRef = useRef(0);
  const momentumStartTimeRef = useRef(0);

  const [hoverPaused, setHoverPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const paused = hoverPaused || isDragging;

  const looped = useMemo(() => {
    // Two identical sequences for a seamless marquee loop.
    // Repeat within each sequence so the logos fill the viewport (no empty gaps).
    const sequenceRepeats = 6;
    const sequence = [];
    for (let i = 0; i < sequenceRepeats; i += 1) {
      for (const venture of ventures) sequence.push(venture);
    }
    return [...sequence, ...sequence];
  }, [ventures]);

  const isVertical = axis === 'y';
  const enableMomentum = isVertical;

  const applyTransform = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const base = baseWidthRef.current;
    if (base > 1) {
      offsetRef.current = ((offsetRef.current % base) + base) % base;
    }

    track.style.transform = isVertical
      ? `translate3d(0, ${-offsetRef.current}px, 0)`
      : `translate3d(${-offsetRef.current}px, 0, 0)`;
  }, [isVertical]);

  useEffect(() => {
    const measure = () => {
      const track = trackRef.current;
      if (!track) return;
      // Track contains two identical halves.
      const base = Math.max(1, (isVertical ? track.scrollHeight : track.scrollWidth) / 2);
      baseWidthRef.current = base;
      applyTransform();
    };

    // Measure after paint.
    const id = window.requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener('resize', measure);
    };
  }, [applyTransform, isVertical, looped.length]);

  useEffect(() => {
    lastTimeRef.current = 0;

    const tick = (time) => {
      if (!lastTimeRef.current) lastTimeRef.current = time;
      const dt = Math.min(0.04, (time - lastTimeRef.current) / 1000);
      lastTimeRef.current = time;

      const base = baseWidthRef.current;
      if (!prefersReducedMotion && !paused && base > 1) {
        if (enableMomentum && momentumActiveRef.current) {
          offsetRef.current += momentumVelocityRef.current * dt;

          const durationMs = 2200;
          const elapsedMs = time - momentumStartTimeRef.current;

          // Exponential decay so it glides, then eases out.
          const decayPerSecond = 2.3; // ~1% remaining after ~2s
          momentumVelocityRef.current *= Math.exp(-decayPerSecond * dt);

          if (elapsedMs >= durationMs || Math.abs(momentumVelocityRef.current) < 6) {
            momentumActiveRef.current = false;
            momentumVelocityRef.current = 0;
          }
        } else {
          const speedPxPerSecond = base / secondsPerLoop;
          offsetRef.current += speedPxPerSecond * dt;
        }
      }

      applyTransform();
      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTimeRef.current = 0;
    };
  }, [applyTransform, enableMomentum, paused, prefersReducedMotion, secondsPerLoop]);

  const startDrag = (event) => {
    if (event.button !== 0) return;

    pointerIdRef.current = event.pointerId;

    isDraggingRef.current = true;
    setIsDragging(true);

    momentumActiveRef.current = false;
    momentumVelocityRef.current = 0;

    dragStartXRef.current = isVertical ? event.clientY : event.clientX;
    dragStartOffsetRef.current = offsetRef.current;
    dragLastTimeRef.current = event.timeStamp;
    dragLastOffsetRef.current = offsetRef.current;
    dragVelocityRef.current = 0;
    dragMovedRef.current = false;
    suppressNextClickRef.current = false;
    lastTimeRef.current = 0;
  };

  const moveDrag = (event) => {
    if (!isDraggingRef.current) return;

    const delta = isVertical ? event.clientY - dragStartXRef.current : event.clientX - dragStartXRef.current;
    const absDelta = Math.abs(delta);

    // Don't start the marquee drag until the pointer has clearly moved.
    // This keeps normal clicks (for links) reliable.
    if (!dragMovedRef.current && absDelta <= 8) return;

    if (!dragMovedRef.current) {
      dragMovedRef.current = true;
      suppressNextClickRef.current = true;
      viewportRef.current?.setPointerCapture?.(pointerIdRef.current ?? event.pointerId);
    }

    offsetRef.current = dragStartOffsetRef.current - delta;

    if (enableMomentum) {
      const dt = (event.timeStamp - dragLastTimeRef.current) / 1000;
      if (dt > 0) {
        const v = (offsetRef.current - dragLastOffsetRef.current) / dt;
        // Light smoothing to reduce jitter.
        dragVelocityRef.current = dragVelocityRef.current * 0.75 + v * 0.25;
        dragLastTimeRef.current = event.timeStamp;
        dragLastOffsetRef.current = offsetRef.current;
      }
    }

    applyTransform();
  };

  const endDrag = () => {
    if (!isDraggingRef.current) return;

    const viewport = viewportRef.current;
    const pid = pointerIdRef.current;
    if (viewport?.hasPointerCapture?.(pid)) {
      viewport.releasePointerCapture?.(pid);
    }

    pointerIdRef.current = null;
    isDraggingRef.current = false;
    setIsDragging(false);

    if (enableMomentum && dragMovedRef.current && !prefersReducedMotion) {
      const maxVelocity = 2800;
      const minVelocity = 160;
      const v = Math.max(-maxVelocity, Math.min(maxVelocity, dragVelocityRef.current));

      if (Math.abs(v) >= minVelocity) {
        momentumActiveRef.current = true;
        momentumVelocityRef.current = v;
        momentumStartTimeRef.current = performance.now();
      }
    }

    lastTimeRef.current = 0;
  };

  return (
    <section className={isVertical ? 'ticker ticker--vertical' : 'ticker'} aria-label="Venture logos">
      <div
        className={isVertical ? 'tickerViewport isVertical' : 'tickerViewport'}
        ref={viewportRef}
        onClickCapture={(e) => {
          if (!suppressNextClickRef.current) return;
          e.preventDefault();
          e.stopPropagation();
          suppressNextClickRef.current = false;
        }}
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div className={paused ? 'tickerTrack isPaused' : 'tickerTrack'} ref={trackRef}>
          {looped.map((venture, idx) => {
            const instanceKey = `${venture.id}-${idx}`;

            return (
              <button
                key={instanceKey}
                type="button"
                className={`tickerLogoButton tickerLogoButton--${venture.id}`}
                aria-label={venture.name}
                onPointerEnter={() => setHoverPaused(true)}
                onPointerLeave={(event) => {
                  const nextTarget = event.relatedTarget;
                  if (nextTarget?.closest?.('.tickerLogoButton')) return;
                  setHoverPaused(false);
                }}
                onClick={() => {
                  onLogoClick?.(venture);
                }}
              >
                <img
                  className={`tickerLogo tickerLogo--${venture.id}`}
                  src={getPublicLogoUrl(venture.filename)}
                  alt={`${venture.name} logo`}
                  draggable={false}
                  loading="lazy"
                />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (!window.matchMedia) return;
    const media = window.matchMedia(query);
    const update = () => setMatches(Boolean(media.matches));
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, [query]);

  return matches;
}

function HomePage() {
  const isMobile = useMediaQuery('(max-width: 900px)');
  const navigate = useNavigate();
  const desktopPortraitUrl = getPublicLogoUrl('arsalandesktop.png');
  const mobileSecondsPerLoop = 80;

  const handleLogoClick = useCallback(
    (venture) => {
      if (!venture?.id) return;

      if (venture.id === 'model') {
        navigate('/model');
        return;
      }

      const href = LOGO_LINKS[venture.id];
      if (!href) return;
      openExternalLink(href);
    },
    [navigate]
  );

  return (
    <div className="page">
      <h1 className="srOnly">311ALAN</h1>

      <main className="desktopHero" aria-label="311ALAN">
        <img
          className="desktopHeroPortrait"
          src={desktopPortraitUrl}
          alt="Arsalan Ihsan"
        />
        <img
          className="topRightLogo"
          src={getPublicLogoUrl('311ALAN Main 2.PNG')}
          alt="311ALAN"
        />

        <div className="desktopTickerStrip" aria-label="Venture logos">
          <LogoTicker
            ventures={VENTURES}
            axis={isMobile ? 'y' : 'x'}
            secondsPerLoop={isMobile ? mobileSecondsPerLoop : 160}
            onLogoClick={handleLogoClick}
          />
        </div>
      </main>
    </div>
  );
}

function ModelPage() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [revealedCount, setRevealedCount] = useState(0);
  const [isRevealing, setIsRevealing] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);

  const images = useMemo(
    () => [
      'arsalanmodel/DSC06561.jpg',
      'arsalanmodel/Republic V.2-240.JPG',
      'arsalanmodel/IMG_2004.JPG',
      'arsalanmodel/IMG_2317.JPG',
      'arsalanmodel/IMG_1203.jpg',
      'arsalanmodel/IMG_3315.JPG',
    ],
    []
  );

  const cells = useMemo(() => {
    // 3x3 grid indices:
    // 0 1 2
    // 3 4 5
    // 6 7 8
    const next = Array(9).fill(null);
    next[0] = images[0];
    next[1] = images[1];
    next[2] = images[2];
    next[3] = images[3];
    next[4] = images[4];
    next[8] = images[5];
    return next;
  }, [images]);

  useEffect(() => {
    if (!isRevealing) return;

    if (prefersReducedMotion) {
      setRevealedCount(9);
      setIsRevealing(false);
      return;
    }

    const intervalMs = 160;
    const id = window.setInterval(() => {
      setRevealedCount((current) => {
        const next = Math.min(9, current + 1);
        if (next >= 9) {
          window.clearInterval(id);
          setIsRevealing(false);
        }
        return next;
      });
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [isRevealing, prefersReducedMotion]);

  useEffect(() => {
    // Auto-start the reveal sequence when landing on /model.
    // Start with the first tile revealed.
    setRevealedCount(1);
    setIsRevealing(true);
  }, []);

  useEffect(() => {
    if (!fullscreenImage) return;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setFullscreenImage(null);
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [fullscreenImage]);

  return (
    <div className="modelPage" aria-label="Model">
      <div className="modelGrid" aria-label="Model grid">
        {cells.map((filename, idx) => (
          <div
            key={idx}
            className={
              idx < revealedCount || (idx === 8 && revealedCount > 0)
                ? 'modelCell isLoaded'
                : 'modelCell'
            }
          >
            {filename ? (
              <img
                className="modelImg"
                src={getPublicLogoUrl(filename)}
                alt="Model"
                loading="lazy"
                onClick={() => {
                  if (idx >= revealedCount && !(idx === 8 && revealedCount > 0)) return;
                  setFullscreenImage(filename);
                }}
              />
            ) : null}
          </div>
        ))}
      </div>

      {fullscreenImage ? (
        <div
          className="modelLightbox"
          role="dialog"
          aria-label="Fullscreen image"
        >
          <button
            type="button"
            className="modelLightboxClose"
            aria-label="Close fullscreen"
            onClick={() => setFullscreenImage(null)}
          >
            ×
          </button>
          <img
            className="modelLightboxImg"
            src={getPublicLogoUrl(fullscreenImage)}
            alt="Model fullscreen"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/model" element={<ModelPage />} />
    </Routes>
  );
}

export default App;
